import { forwardRef, memo, useRef } from 'react';
import styled from 'styled-components';
import { applySX, type WithSX } from '@elfsight/lib-utils';
import { createReadableClass, useResizeObserver } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import { Buttons, Heading, Timer } from '../../elements';
import { Deactivation, FreeLink, WidgetBackground } from '../../main';
import { getBorderRadius } from '../../utils';
import { THUMBNAIL_MODE_WIDTH } from '../constants';
import { useLayout } from '../layout-context';
import { getAlignment } from '../utils';

const DESKTOP_PADDINGS = {
  horizontalPadding: 40,
  verticalPadding: 40
};
const MOBILE_PADDINGS = {
  horizontalPadding: 20,
  verticalPadding: 20
};

const GAP = 20;

export const LayoutInline = memo(
  forwardRef<HTMLDivElement, unknown>((_, ref) => {
    const {
      layout,
      widgetAlignment,
      contentAlignment,
      widgetWidth,
      contentWidth,
      theme: {
        themeSettings: { cornerRadius }
      },
      renderThumbnailMode
    } = useSettings();
    const { isMobileBreakpoint } = useLayout();

    const widgetRef = useRef<HTMLDivElement>(null);
    const mergedWidgetRef = useMergedRef(widgetRef, ref);
    const contentRef = useRef<HTMLDivElement>(null);

    const isExactlyInline = layout === 'inline';

    const [widgetRefCurrent, contentRefCurrent] = isExactlyInline
      ? []
      : [widgetRef.current, contentRef.current];
    const [{ width: calculatedWidgetWidth }] =
      useResizeObserver(widgetRefCurrent);
    const [{ width: calculatedContentWidth }] =
      useResizeObserver(contentRefCurrent);

    const {
      widgetWidth: derivedWidgetWidth,
      widgetAlignment: derivedWidgetAlignment
    } = (() => {
      if (renderThumbnailMode) {
        return {
          widgetWidth: THUMBNAIL_MODE_WIDTH,
          widgetAlignment: 'center'
        } as const;
      }

      return {
        widgetWidth: isExactlyInline ? widgetWidth : undefined,
        widgetAlignment: isExactlyInline
          ? getAlignment(widgetAlignment)
          : undefined
      } as const;
    })();
    const derivedContentAlignment = isExactlyInline
      ? 'center'
      : getAlignment(contentAlignment);

    const { horizontalPadding, verticalPadding } = isMobileBreakpoint
      ? MOBILE_PADDINGS
      : DESKTOP_PADDINGS;
    const isContentFullWidth =
      isExactlyInline ||
      calculatedContentWidth + horizontalPadding * 2 === calculatedWidgetWidth;
    const buttonsHorizontalPadding = isContentFullWidth ? horizontalPadding : 0;

    const borderRadius = isExactlyInline
      ? getBorderRadius(cornerRadius, { rounded: 12, circular: 24 })
      : 0;

    return (
      <Container
        $sx={{
          justifyContent: derivedWidgetAlignment
        }}
      >
        <Deactivation childrenRef={mergedWidgetRef}>
          <WidgetBackground
            style={{
              maxWidth: derivedWidgetWidth
            }}
            borderRadius={borderRadius}
            horizontalPadding={horizontalPadding}
            verticalPadding={verticalPadding}
          >
            <Content
              ref={contentRef}
              $sx={{
                textAlign: isExactlyInline ? 'center' : contentAlignment,
                justifyContent: derivedContentAlignment,
                alignItems: derivedContentAlignment,
                maxWidth: !isExactlyInline ? contentWidth : undefined
              }}
            >
              <Heading />

              <Timer />

              <Buttons
                // Нужно чтоб при переключении лейаута сбрасывался скролл
                key={layout}
                horizontalPadding={buttonsHorizontalPadding}
                rtl={derivedContentAlignment === 'flex-end'}
              />

              <FreeLink />
            </Content>
          </WidgetBackground>
        </Deactivation>
      </Container>
    );
  })
);

const Container = styled.div.attrs<WithSX>({
  className: createReadableClass({
    component: 'layout-inline',
    element: 'container'
  })
})`
  ${applySX};
  display: flex;
  width: 100%;
`;

const Content = styled.div.attrs({
  className: createReadableClass({
    component: 'layout-inline',
    element: 'content'
  })
})<WithSX>`
  ${applySX};
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  gap: ${GAP}px;
`;
